// src/pages/ResetPassword.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getAuth,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { useAlert } from "../components/CustomAlert";
import "../styles/Auth.css";

function useQuery() {
  const location = useLocation();
  const search = location.search || "";
  return useMemo(function () {
    return new URLSearchParams(search);
  }, [search]);
}

export default function ResetPassword() {
  const auth = getAuth();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const query = useQuery();

  // 页面状态：checking | ready | submitting | done | error
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const handledRef = useRef(false); // 防止 useEffect 重复执行

  // 1）页面加载时，校验链接上的 oobCode
  useEffect(function () {
    if (handledRef.current) {
      return;
    }
    handledRef.current = true;

    var oobCode = query.get("oobCode");
    var mode = query.get("mode");

    // 如果是 verifyEmail 的链接误进到这里，直接跳去邮箱验证页
    if (mode === "verifyEmail") {
      var newUrl = "/verify-email" + window.location.search;
      navigate(newUrl, { replace: true });
      return;
    }

    // 参数不对，直接报错
    if (!oobCode || mode !== "resetPassword") {
      setStatus("error");
      showAlert(
        "提示 / Notice",
        "重置链接无效或已过期。The reset link is invalid or has expired.",
        "我知道了 / Got it",
        "error",
        function () {
          navigate("/login");
        }
      );
      return;
    }

    // 校验 reset code，拿回邮箱
    verifyPasswordResetCode(auth, oobCode)
      .then(function (mail) {
        if (mail) {
          setEmail(mail);
        }
        setStatus("ready");
      })
      .catch(function (err) {
        console.error("verifyPasswordResetCode error:", err);
        setStatus("error");
        showAlert(
          "提示 / Notice",
          "重置链接无效或已过期。The reset link is invalid or has expired.",
          "我知道了 / Got it",
          "error",
          function () {
            navigate("/login");
          }
        );
      });
  }, [auth, navigate, query, showAlert]);

  // 2）提交新密码
  async function handleSubmit(e) {
    e.preventDefault();

    if (status !== "ready") {
      return;
    }

    var oobCode = query.get("oobCode");
    if (!oobCode) {
      return;
    }

    // 基本校验
    if (!newPwd || newPwd.length < 6) {
      showAlert(
        "提示 / Notice",
        "新密码至少 6 位。The new password must be at least 6 characters.",
        "我知道了 / Got it",
        "error"
      );
      return;
    }

    if (newPwd !== confirmPwd) {
      showAlert(
        "提示 / Notice",
        "两次输入的密码不一致。The two passwords do not match.",
        "我知道了 / Got it",
        "error"
      );
      return;
    }

    setStatus("submitting");

    try {
      await confirmPasswordReset(auth, oobCode, newPwd);
      setStatus("done");
      showAlert(
        "密码已更新 / Password updated",
        "您的密码已成功重置，请使用新密码登录。Your password has been reset. Please log in with your new password.",
        "去登录 / Go to login",
        "success",
        function () {
          navigate("/login");
        }
      );
    } catch (err) {
      console.error("confirmPasswordReset error:", err);
      setStatus("ready");
      showAlert(
        "提示 / Notice",
        "重置失败，请稍后再试。Failed to reset password, please try again.",
        "我知道了 / Got it",
        "error"
      );
    }
  }

  // ============ 视图部分 ============

  return (
    <div className="verify-page">
      <div className="verify-card">
        <h2 className="verify-title">重置密码 / Reset password</h2>

        {status === "checking" && (
          <p className="verify-lead">正在校验重置链接，请稍候… / Checking reset link…</p>
        )}

        {status === "ready" && (
          <>
            {email ? (
              <p className="verify-lead">
                为以下邮箱重置密码：{email}
                <br />
                Reset password for: {email}
              </p>
            ) : (
              <p className="verify-lead">
                请设置新的登录密码。Please set your new password.
              </p>
            )}

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <label>
                新密码 / New password
                <input
                  type="password"
                  value={newPwd}
                  onChange={function (e) {
                    setNewPwd(e.target.value);
                  }}
                  placeholder="至少 6 位 / At least 6 characters"
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </label>

              <label>
                再输一次新密码 / Confirm new password
                <input
                  type="password"
                  value={confirmPwd}
                  onChange={function (e) {
                    setConfirmPwd(e.target.value);
                  }}
                  placeholder="请再次输入新密码 / Please re-enter new password"
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </label>

              <button
                type="submit"
                className="button"
                disabled={status === "submitting"}
              >
                {status === "submitting"
                  ? "提交中… / Submitting…"
                  : "提交 / Submit"}
              </button>

              <button
                type="button"
                className="linkButton"
                style={{ marginTop: 12, display: "block", width: "100%" }}
                onClick={function () {
                  navigate("/login");
                }}
              >
                返回登录 / Back to login
              </button>
            </form>
          </>
        )}

        {status === "error" && (
          <p className="verify-lead">
            重置链接无效或已过期。The reset link is invalid or has expired.
          </p>
        )}
      </div>
    </div>
  );
}
